import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';


export default function UserList() {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [userRole, setUserRole] = useState(null);
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { logout } = useAuth();

    useEffect(() => {
        fetchUserRole();
        fetchUsers();
    }, []);

    const fetchUserRole = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`,
                { withCredentials: true }
            );
            setUserRole(response.data.role);
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            if (error.response?.status === 401) {
                logout();
            } else {
                setError('Kunne ikke laste brukere');
            }
        }
    };

    const handleUserClick = (userId) => {
        navigate(`/users/${userId}`);
    };

    const isSuperAdmin = userRole === 'superadmin';

    // Function to filter subscription statuses based on priority rules
    const filterSubscriptionStatuses = (statusesString) => {
        if (!statusesString) return [];
        
        const statuses = statusesString.split(',').map(s => s.trim().toLowerCase());
        const filtered = [...statuses];
        
        // If active and pending both exist, remove pending
        if (filtered.includes('active') && filtered.includes('pending')) {
            const index = filtered.indexOf('pending');
            filtered.splice(index, 1);
        }
        
        // If trialing and pending both exist, remove pending
        if (filtered.includes('trialing') && filtered.includes('pending')) {
            const index = filtered.indexOf('pending');
            filtered.splice(index, 1);
        }
        
        // If active and canceled both exist, remove canceled
        if (filtered.includes('active') && filtered.includes('canceled')) {
            const index = filtered.indexOf('canceled');
            filtered.splice(index, 1);
        }
        
        // Return original case statuses (preserve capitalization)
        return statusesString.split(',').map(s => s.trim()).filter(status => 
            filtered.includes(status.toLowerCase())
        );
    };

    // Filter users based on device filter, status filter, and search term
    const filteredUsers = users.filter(user => {
        if (user.role === 'guest') return false;
        
        // Search filter (name and email)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const nameMatch = user.name?.toLowerCase().includes(searchLower);
            const emailMatch = user.email?.toLowerCase().includes(searchLower);
            if (!nameMatch && !emailMatch) return false;
        }
        
        // Device filter
        if (deviceFilter !== 'all' && user.device !== deviceFilter) return false;
        
        // Status filter
        if (statusFilter !== 'all') {
            if (statusFilter === 'no-subscription') {
                // Show users with no subscription statuses
                if (user.subscriptionStatuses) return false;
            } else {
                // Show users with specific subscription status
                if (!user.subscriptionStatuses) return false;
                const userStatuses = user.subscriptionStatuses.split(',').map(s => s.trim().toLowerCase());
                if (!userStatuses.includes(statusFilter.toLowerCase())) return false;
            }
        }
        
        return true;
    });

    // Calculate device counts
    const deviceCounts = users.reduce((counts, user) => {
        if (user.role === 'guest') return counts;
        const device = user.device || 'unknown';
        counts[device] = (counts[device] || 0) + 1;
        return counts;
    }, {});

    // Calculate status counts using filtered statuses
    const statusCounts = users.reduce((counts, user) => {
        if (user.role === 'guest') return counts;
        if (user.subscriptionStatuses) {
            const filteredStatuses = filterSubscriptionStatuses(user.subscriptionStatuses);
            filteredStatuses.forEach(status => {
                const statusLower = status.toLowerCase();
                counts[statusLower] = (counts[statusLower] || 0) + 1;
            });
        } else {
            counts['no-subscription'] = (counts['no-subscription'] || 0) + 1;
        }
        return counts;
    }, {});

    const androidCount = deviceCounts['android'] || 0;
    const iosCount = deviceCounts['ios'] || 0;
    const totalCount = users.length;

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    width: '100%'
                }}>
                    <h1>{isSuperAdmin ? 'Alle brukere' : 'Brukere i din bedrift'}</h1>
                    <button 
                        onClick={logout}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            minWidth: '100px',
                            width: 'auto'
                        }}
                    >
                        <i className="fas fa-sign-out-alt"></i> Logg ut
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Device Filter and Counts */}
            <div className="device-filter-section">
                <div className="device-counts">
                    <span className="count-item">
                        <i className="fab fa-android"></i> Android: {androidCount}
                    </span>
                    <span className="count-item">
                        <i className="fab fa-apple"></i> iOS: {iosCount}
                    </span>
                    <span className="count-item">
                        <i className="fas fa-users"></i> Totalt: {totalCount}
                    </span>
                </div>
                <div className="filter-controls">
             
                    <div className="device-filter">
                        <label htmlFor="deviceFilter">Filtrer etter enhet:</label>
                        <select 
                            id="deviceFilter"
                            value={deviceFilter} 
                            onChange={(e) => setDeviceFilter(e.target.value)}
                            className="device-filter-select"
                        >
                            <option value="all">Alle enheter</option>
                            <option value="android">Android</option>
                            <option value="ios">iOS</option>
                            <option value="unknown">Ukjent</option>
                        </select>
                    </div>
                    <div className="status-filter">
                        <label htmlFor="statusFilter">Filtrer etter status:</label>
                        <select 
                            id="statusFilter"
                            value={statusFilter} 
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="status-filter-select"
                        >
                            <option value="all">Alle statuser</option>
                            <option value="active">Aktiv</option>
                            <option value="trialing">Prøveperiode</option>
                            <option value="pending">Venter</option>
                            <option value="canceled">Kansellert</option>
                            <option value="past_due">Forfalt</option>
                            <option value="unpaid">Ubetalt</option>
                            <option value="no-subscription">Ingen abonnement</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="search-container">
                <div className="search-filter">
                        <label htmlFor="searchTerm">
                            <i className="fas fa-search"></i> Søk:
                        </label>
                        <input
                            type="text"
                            id="searchTerm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Søk etter navn eller e-post..."
                            className="search-input"
                        />
                    </div>
            </div>
            <div className="list-container">
                {filteredUsers.map((user) => (
                    <div 
                        key={user.idusers} 
                        className="list-item clickable"
                        onClick={() => handleUserClick(user.idusers)}
                    >
                        <div className="user-info">
                            <h3>{user.name}</h3>
                            <p className="user-email">{user.email}</p>
                            <p className="user-role">Rolle: {user.role}</p>
                            <p className="user-device user-role">
                                {/* <i className={`fab fa-${user.device === 'android' ? 'android' : user.device === 'ios' ? 'apple' : 'question'}`}></i> */}
                                Enhet: {user.device ? user.device.toLowerCase() : 'Ukjent'}
                            </p>
                            {user.businessName && (
                                <p className="user-business">Bedrift: {user.businessName}</p>
                            )}
                            {user.subscriptionStatuses && (() => {
                                const filteredStatuses = filterSubscriptionStatuses(user.subscriptionStatuses);
                                return (
                                    <div className="user-subscriptions">
                                        <p className="subscription-count">
                                            Abonnementer: {filteredStatuses.length}
                                        </p>
                                        <div className="subscription-statuses">
                                            {filteredStatuses.map((status, index) => (
                                                <span 
                                                    key={index} 
                                                    className={`status-tag ${status.trim().toLowerCase()}`}
                                                >
                                                    {status.trim()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}
                                     {!user.subscriptionStatuses && (
                                <div className="user-subscriptions">
                                    <p className="subscription-count">
                                            Abonnementer: 0
                                        </p>
                                    <div className="subscription-statuses">
                                        {/* {user.subscriptionStatuses.split(',').map((status, index) => { */}
                                            <span 
                                                // key={index} 
                                                className={`status-tag no-subscription`}
                                            >
                                                Ingen abonnement
                                            </span>
                                        {/* ))} */}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
} 